import { normalizeNewlines, sanitizeFilename, timestamp } from './formatters';
import { downloadFile } from './fileHandlers';

export const extractQAForAssistant = (index, messages) => {
	const a = messages[index];
	const q = messages[index - 1];
	if (!a || a.role !== 'assistant' || !q || q.role !== 'user') return null;
	return { q, a, qIndex: index - 1, aIndex: index };
};

// Helper function to prepare Q&A content consistently
const prepareQAContent = (qa) => {
	return {
		question: normalizeNewlines(qa.q.content),
		answer: normalizeNewlines(qa.a.content)
	};
};

export const toMarkdown = (qa) => {
	const { question, answer } = prepareQAContent(qa);
	return `**سوال**\n\n${question}\n\n**پاسخ**\n\n${answer}`;
};

export const toTxt = (qa) => {
	const { question, answer } = prepareQAContent(qa);
	const Q = markdownToTxt(question);
	const A = markdownToTxt(answer);
	const RLM = '\u200F'; // Right-to-Left Mark
	return `${RLM}سوال:\n${Q}\n\nپاسخ:\n${A}`;
};

// Helper function to convert markdown to plain text
export const markdownToTxt = (markdownText) => {
	if (!markdownText) return '';

	return markdownText
		// Remove headers (replace #, ##, ### with empty string)
		.replace(/^#+\s+/gm, '')
		// Remove bold/italic (remove ** and * markers)
		.replace(/\*\*(.*?)\*\*/g, '$1')
		.replace(/\*(.*?)\*/g, '$1')
		// Remove code blocks (keep content)
		.replace(/```[\s\S]*?\n([\s\S]*?)```/g, '$1')
		.replace(/`([^`]+)`/g, '$1')
		// Remove links (keep link text only) [text](url) -> text
		.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
		// Remove images (keep alt text only) ![alt](url) -> alt
		.replace(/!\[([^\]]+)\]\([^)]+\)/g, '$1')
		// Remove blockquotes (remove > markers)
		.replace(/^>\s+/gm, '')
		// Remove horizontal rules
		.replace(/^[-*_]{3,}\s*$/gm, '')
		// Convert markdown lists to simple indented lists
		.replace(/^[-*+]\s+/gm, '• ')
		.replace(/^\d+\.\s+/gm, '')
		// Remove HTML tags (basic protection)
		.replace(/<[^>]*>/g, '')
		// Normalize whitespace and clean up
		.replace(/\n{3,}/g, '\n\n')
		.trim();
};

const markdownToWord = (qa) => {
	if (!qa) return '';

	const { question, answer } = prepareQAContent(qa);
	let content = `<h2>سوال</h2>\n\n${question}\n\n<h2>پاسخ</h2>\n\n${answer}`;

	// Process transformations in a controlled manner
	const processTransformations = (text, transforms) => {
		let result = text;
		transforms.forEach(([regex, replacement]) => {
			result = result.replace(regex, replacement);
		});
		return result;
	};

	// Step 1: Handle code blocks and inline code
	content = processTransformations(content, [
		[/```(\w+)?\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>'],
		[/`([^`]+)`/g, '<code>$1</code>']
	]);

	// Step 2: Handle headers
	content = processTransformations(content, [
		[/^# (.*$)/gim, '<h1>$1</h1>'],
		[/^## (.*$)/gim, '<h2>$1</h2>'],
		[/^### (.*$)/gim, '<h3>$1</h3>'],
		[/^#### (.*$)/gim, '<h4>$1</h4>']
	]);

	// Step 3: Handle formatting
	content = processTransformations(content, [
		[/\*\*(.*?)\*\*/g, '<strong>$1</strong>'],
		[/\*(.*?)\*/g, '<em>$1</em>'],
		[/_(.*?)_/g, '<em>$1</em>']
	]);

	// Step 4: Handle links and images
	content = processTransformations(content, [
		[/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>'],
		[/!\[([^\]]+)\]\(([^)]+)\)/g, '<img alt="$1" src="$2" />']
	]);

	// Step 5: Handle paragraphs and line breaks
	content = content
		.split(/\n\n+/)
		.map(paragraph => {
			const trimmed = paragraph.trim();
			if (!trimmed) return '';

			// Don't wrap existing HTML elements
			if (trimmed.startsWith('<') &&
				(trimmed.includes('<h') ||
					trimmed.includes('<pre') ||
					trimmed.includes('<ul') ||
					trimmed.includes('<ol') ||
					trimmed.includes('<blockquote'))) {
				return trimmed;
			}

			// Convert single newlines to <br> tags within paragraphs
			const withBreaks = trimmed.replace(/\n/g, '<br />');
			return `<p>${withBreaks}</p>`;
		})
		.join('\n\n');

	return `<!DOCTYPE html>
<html dir='rtl'>
<head>
    <meta charset="UTF-8">
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; text-align: right; direction: rtl;}
        /* Add your styles here */
    </style>
</head>
<body>
    ${content}
</body>
</html>`;
};

export const toJson = (qa, chatId) => {
	const { question, answer } = prepareQAContent(qa);
	return JSON.stringify({
		question: question,
		answer: answer
	}, null, 2);
};

export const markdownToHtml = (qa) => {
	if (!qa) return '';

	const { question, answer } = prepareQAContent(qa);
	let content = `<h2>سوال</h2>\n\n${question}\n\n<h2>پاسخ</h2>\n\n${answer}`;

	// Process transformations in order of specificity
	const transformations = [
		// Code blocks first (highest priority)
		[/```(\w+)?\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>'],
		[/`([^`]+)`/g, '<code>$1</code>'],

		// Headers
		[/^# (.*$)/gim, '<h1>$1</h1>'],
		[/^## (.*$)/gim, '<h2>$1</h2>'],
		[/^### (.*$)/gim, '<h3>$1</h3>'],
		[/^#### (.*$)/gim, '<h4>$1</h4>'],
		[/^##### (.*$)/gim, '<h5>$1</h5>'],
		[/^###### (.*$)/gim, '<h6>$1</h6>'],

		// Bold and Italic
		[/\*\*(.*?)\*\*/g, '<strong>$1</strong>'],
		[/\*(.*?)\*/g, '<em>$1</em>'],
		[/_(.*?)_/g, '<em>$1</em>'],

		// Strikethrough
		[/~~(.*?)~~/g, '<del>$1</del>'],

		// Links
		[/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>'],

		// Images
		[/!\[([^\]]+)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="markdown-image" />'],

		// Blockquotes
		[/^> (.*$)/gim, '<blockquote>$1</blockquote>'],

		// Horizontal rules
		[/^[-*_]{3,}$/gm, '<hr class="markdown-hr" />'],

		// Unordered lists
		[/^\s*[-*+] (.*$)/gim, '<li>$1</li>'],

		// Ordered lists
		[/^\s*\d+\. (.*$)/gim, '<li>$1</li>'],
	];

	// Apply all transformations
	transformations.forEach(([regex, replacement]) => {
		content = content.replace(regex, replacement);
	});

	// Process lists properly
	content = processLists(content);

	// Handle paragraphs and line breaks
	content = content
		.split(/\n\n+/)
		.map(paragraph => {
			const trimmed = paragraph.trim();
			if (!trimmed) return '';

			// Don't wrap existing block elements
			if (isBlockElement(trimmed)) {
				return trimmed;
			}

			// Convert single newlines to <br> tags within paragraphs
			const withBreaks = trimmed.replace(/\n/g, '<br />');
			return `<p>${withBreaks}</p>`;
		})
		.join('\n');

	return `<div dir="rtl" style="direction: rtl; text-align: right;">${content}</div>`;;
};

// Helper function to process lists properly
const processLists = (content) => {
	// Process unordered lists
	content = content.replace(/(<li>.*?<\/li>)(?=\s*<[^li]|$)/gs, (match) => {
		return `<ul>${match}</ul>`;
	});

	// Process ordered lists (look for numbered list items)
	content = content.replace(/(<li>.*?<\/li>)(?=\s*<[^li]|$)/gs, (match) => {
		// Check if this looks like an ordered list (contains numbers)
		if (match.match(/<li>\d+\./)) {
			return `<ol>${match}</ol>`;
		}
		return match;
	});

	return content;
};

// Helper function to check if text is already a block element
const isBlockElement = (text) => {
	const blockElements = [
		'<h1', '<h2', '<h3', '<h4', '<h5', '<h6',
		'<pre', '<ul', '<ol', '<blockquote', '<hr',
		'<table', '<div', '<form', '<nav', '<header', '<footer'
	];

	return blockElements.some(element => text.startsWith(element));
};

export const handleExport = (index, format, messages, chatIds, currentChatIndex) => {
	const qa = extractQAForAssistant(index, messages);
	if (!qa) {
		alert('برای خروجی، باید این پیام پاسخ مستقیمِ یک سؤال کاربر باشد.');
		return;
	}
	const chatId = chatIds[currentChatIndex] || 'chat';
	const base = sanitizeFilename(`QA_${chatId}_${timestamp()}`);

	if (format === 'md') downloadFile(`${base}.md`, toMarkdown(qa), 'text/markdown;charset=utf-8');
	else if (format === 'txt') downloadFile(`${base}.txt`, toTxt(qa), 'text/plain;charset=utf-8');
	else if (format === 'json') downloadFile(`${base}.json`, toJson(qa, chatId), 'application/json;charset=utf-8');
	else if (format === 'html') downloadFile(`${base}.html`, markdownToHtml(qa), 'text/html;charset=utf-8');
	else if (format === 'doc') downloadFile(`${base}.doc`, markdownToWord(qa), 'application/msword;charset=utf-8');
};